import { Stack, useTheme } from '@mui/material';
import { LinkedinTypes } from 'enums';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LinkedinAccountSyncStages } from '../linkedin-accounts.types';
import { SyncProcessingStatuses } from './app-sync-dialog.types';
import StepDivider from './step-divider';
import StepItem from './step-item';

interface Props {
  syncStage: LinkedinAccountSyncStages | undefined;
  status: SyncProcessingStatuses;
  liProductType: LinkedinTypes;
  /** Region der festen IP, etwa „München, Deutschland". */
  locationLabel?: string;
}

type ConnectStageId = 'ip' | 'device' | 'signin' | 'twofa' | 'contract' | 'done';

/**
 * Welcher der sechs Punkte gerade läuft.
 *
 * Der waagerechte Stepper zeigte fünf technische Namen (`extractingQueryIDs`), die den
 * Kunden nichts sagten und beim Warten auf die 2FA einfach stehenblieben. Hier laufen
 * dieselben Zustände auf die sechs Dinge zusammen, die tatsächlich passieren — und der
 * Punkt, an dem der Kunde selbst dran ist, ist einer davon.
 */
const resolveActiveStage = (syncStage: LinkedinAccountSyncStages | undefined, status: SyncProcessingStatuses): ConnectStageId => {
  if (status === SyncProcessingStatuses.TWO_FA) return 'twofa';
  if (status === SyncProcessingStatuses.CHOOSE_CONTRACT) return 'contract';
  if (
    status === SyncProcessingStatuses.CAPTCHA ||
    status === SyncProcessingStatuses.THIRD_PARTY ||
    status === SyncProcessingStatuses.THIRD_PARTY_TEXT
  ) {
    return 'signin';
  }

  switch (syncStage) {
    case LinkedinAccountSyncStages.LOADING_PAGE:
    case LinkedinAccountSyncStages.SIGNING_IN:
      return 'signin';
    case LinkedinAccountSyncStages.EXTRACTING_COOKIES:
    case LinkedinAccountSyncStages.EXTRACTING_QUERY_IDS:
      return 'done';
    case LinkedinAccountSyncStages.IN_QUEUE:
    default:
      return 'device';
  }
};

const ConnectProgress: React.FC<Props> = ({ syncStage, status, liProductType, locationLabel }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const needsContract = liProductType === LinkedinTypes.RECRUITER || liProductType === LinkedinTypes.SALES_NAV;

  const stages: { id: ConnectStageId; text: string; subText?: string }[] = [
    {
      id: 'ip',
      text: t('appSyncConnectStageIp', 'Feste IP gesichert'),
      subText: locationLabel,
    },
    {
      id: 'device',
      text: t('appSyncConnectStageDevice', 'Gerät vorbereitet'),
      subText: t('appSyncConnectStageDeviceHint', 'LinkedIn-App startklar'),
    },
    {
      id: 'signin',
      text: t('appSyncConnectStageSignIn', 'Bei LinkedIn anmelden'),
      subText: t('appSyncConnectStageSignInHint', 'Wir öffnen die App und melden uns an…'),
    },
    {
      id: 'twofa',
      text: t('appSyncConnectStageTwoFa', 'Bestätigung in zwei Schritten'),
      subText: t('appSyncConnectStageTwoFaHint', 'Wir fragen dich gleich nach dem Code'),
    },
    ...(needsContract
      ? [
          {
            id: 'contract' as ConnectStageId,
            text: t('appSyncConnectStageContract', 'Vertrag wählen'),
            subText: t('appSyncConnectStageContractHint', 'Nur bei Recruiter / Sales Navigator'),
          },
        ]
      : []),
    {
      id: 'done',
      text: t('appSyncConnectStageDone', 'Fertig'),
    },
  ];

  // Never step back: after the code the login reports "signing in" again, and a pick of the
  // contract ends in cookie extraction — the list must not jump backwards meanwhile.
  const reachedIndex = React.useRef(-1);
  const currentIndex = stages.findIndex((stage) => stage.id === resolveActiveStage(syncStage, status));
  reachedIndex.current = Math.max(reachedIndex.current, currentIndex);
  const activeIndex = reachedIndex.current;

  return (
    <Stack>
      {stages.map((stage, index) => {
        const isDone = index < activeIndex;
        const isActive = index === activeIndex;
        const color = isDone ? theme.palette.success.main : isActive ? theme.palette.primary.main : theme.palette.greys.light;

        return (
          <React.Fragment key={stage.id}>
            <StepItem
              status={isDone ? 'completed' : isActive ? 'active-loading' : 'not-active'}
              text={stage.text}
              subText={stage.subText}
              color={color}
            />
            {index !== stages.length - 1 ? (
              <StepDivider
                status={isDone ? 'to-active' : 'to-not-active'}
                height={'22px'}
                color={isDone ? theme.palette.success.main : theme.palette.greys.lighter}
              />
            ) : null}
          </React.Fragment>
        );
      })}
    </Stack>
  );
};

export default ConnectProgress;
